import type { ListFilesResponseDto } from '@inference.engine/workspace-api-client/web';
import type { QueryClient } from '@tanstack/react-query';
import type { CommentTargetRef } from '@/entities/project-comment';

/**
 * Keeps the comment-count badges (file cards / folder rows) in sync
 * after a comment is added or removed. Only file comments roll up into folder
 * counts.
 *
 * The folder-files list is *active* behind the asset detail view (it drives the
 * filmstrip), so we patch the file's `commentCount` in place rather than
 * invalidating — a refetch there would reload the preview/filmstrip mid-comment.
 * The folder-level rollups (`list`/`detail`) carry their own aggregate counts we
 * can't derive here, so those are still invalidated; they're cheap and usually
 * inactive while commenting.
 */
export function syncFolderCommentCounts(
  queryClient: QueryClient,
  projectId: string,
  target: CommentTargetRef,
  delta: number,
): void {
  if (target.targetType !== 'file') return;

  const fileId = target.targetId;

  queryClient.setQueriesData<ListFilesResponseDto>(
    {
      queryKey: ['project', projectId, 'folders'],
      predicate: (q) => q.queryKey[4] === 'files',
    },
    (old) =>
      old
        ? {
            ...old,
            items: old.items.map((f) =>
              f.id === fileId ? { ...f, commentCount: Math.max(0, f.commentCount + delta) } : f,
            ),
          }
        : old,
  );

  void queryClient.invalidateQueries({ queryKey: ['project', projectId, 'folders', 'list'] });
  void queryClient.invalidateQueries({ queryKey: ['project', projectId, 'folders', 'detail'] });
}
